/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/ban-types */
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  ButtonProps,
  Drawer,
  Paper,
  SvgIconTypeMap,
  Tooltip,
  useTheme,
} from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import { Resizable } from "re-resizable";
import React, { RefObject, useEffect, useMemo, useState } from "react";
import { Choose, classNames } from "react-extras";
import { useTranslation } from "react-i18next";
import { PortDetailsDto } from "~/api/GeneratedApi";
import { useAppSelector } from "~/redux/store/store";
import MapPortCallsDrawerContent from "./MapPortCallsDrawerContent";
import { PortBerthOrders } from "./PortBerthOrders";

interface Props {
  port: PortDetailsDto;
  parentWidth: number;
  parentHeight: number;
  map?: Map;
}

const useStyles = makeStyles((theme) => ({
  drawerRoot: {
    width: "60vw",
    top: "106px !important",
    position: "absolute !important" as "absolute",
    /*  transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }), */
  },
  drawerClosed: {
    width: 0,
  },
  drawerPaper: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    height: "100%",
    position: "absolute",
    overflow: "visible",
    zIndex: theme.zIndex.drawer,
    borderBottomRightRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  drawerContent: {
    /* overflow: "hidden", */
  },
}));

interface MapDrawerButtonProps {
  onClick?: () => void;
  isOpenButton?: boolean;
  offset?: number;
  rotation?: number;
  icon?: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
}

const MapDrawerButton = React.forwardRef(
  (
    {
      onClick,
      isOpenButton,
      offset,
      icon,
      rotation,
      ...rest
    }: MapDrawerButtonProps & ButtonProps,
    ref,
  ): JSX.Element => (
    <Box sx={{ position: "relative", width: "100%", height: 0 }}>
      <Button
        {...rest}
        ref={ref as RefObject<HTMLButtonElement>}
        sx={{
          backgroundColor: "background.paper",
          borderLeft: "1px solid #878c8e",
          borderTop: "1px solid #878c8e",
          borderRight: "1px solid #878c8e",
          borderRadius: 0,
          borderTopRightRadius: (theme) => theme.shape.borderRadius,
          borderTopLeftRadius: (theme) => theme.shape.borderRadius,
          width: 96,
          height: 36,
          right: {
            xs: `calc(${isOpenButton ? (96 - 36) / 2 : 10}px + ${
              offset ?? 0
            }px)`,
            md: (96 - 36) / 2,
          },
          position: "absolute",
          top: {
            xs: isOpenButton ? "226px" : "-36px",
            md: `calc(${isOpenButton ? "226px" : "120px"} + ${
              offset ?? "0"
            }px)`,
          },
          transform: {
            xs: isOpenButton ? `translateX(100%) rotate(90deg)` : undefined,
            md: `translateX(100%) rotate(90deg)`,
          },
          zIndex: (theme) => theme.zIndex.drawer - 1,
          boxShadow: 6,
          "&:hover": {
            backgroundColor: "background.paper",
          },
        }}
        disableRipple
        onClick={onClick}
        variant="contained"
      >
        <Box
          component={icon ?? KeyboardArrowUpIcon}
          sx={{
            transition: (theme) =>
              theme.transitions.create("transform", {
                duration: theme.transitions.duration.standard,
                easing: theme.transitions.easing.easeInOut,
              }),
            color: "common.black",
            transform: {
              xs:
                // eslint-disable-next-line no-nested-ternary
                rotation !== undefined
                  ? `rotate(${rotation}deg)`
                  : !isOpenButton
                  ? `rotate(-90deg)`
                  : undefined,
              md:
                // eslint-disable-next-line no-nested-ternary
                rotation !== undefined
                  ? `rotate(${rotation}deg)`
                  : !isOpenButton
                  ? `rotate(180deg)`
                  : undefined,
            },
          }}
        />
      </Button>
    </Box>
  ),
);

enum PortTab {
  PortCalls,
  BerthOrders,
}

const MapPortCallsDrawer = ({
  port,
  parentWidth,
  parentHeight,
  map,
}: Props): JSX.Element => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();
  const theme = useTheme();
  const [width, setWidth] = useState<number>(parentWidth * 0.7);
  const [tempWidth, setTempWidth] = useState<number>(parentWidth * 0.7);
  const [tab, setTab] = React.useState<PortTab>(PortTab.PortCalls);
  const { t } = useTranslation("port-calls-drawer");
  const isMobile = useAppSelector((s) => s.general.isMobile);

  const height = useMemo(
    () => parentHeight * (isMobile ? 0.9 : 0.8),
    [isMobile, parentHeight],
  );

  useEffect(() => {
    if (width > parentWidth - 36) {
      setWidth(parentWidth - 36);
      setTempWidth(parentWidth - 36);
    }
  }, [parentWidth, width]);

  const handleDrawerClose = React.useCallback(() => setOpen(false), []);

  return (
    <div id="port-calls-container">
      {!open && <MapDrawerButton onClick={() => setOpen(true)} isOpenButton />}

      <Drawer
        hideBackdrop
        classes={{
          root: classNames(
            classes.drawerRoot,
            !open ? classes.drawerClosed : "",
          ),
          paper: classes.drawerPaper,
        }}
        style={{ width: tempWidth, height }}
        onClose={() => setOpen(false)}
        PaperProps={{ elevation: 10 }}
        open={open}
        ModalProps={{
          disablePortal: true,
        }}
      >
        {open && (
          <>
            <MapDrawerButton
              onClick={() => setOpen(false)}
              isOpenButton={false}
            />
            <Tooltip
              title={
                tab === PortTab.PortCalls
                  ? t("berth-orders-tab").toString()
                  : t("port-calls-tab").toString()
              }
            >
              <MapDrawerButton
                rotation={isMobile ? 0 : -90}
                onClick={() =>
                  setTab(
                    tab === PortTab.PortCalls
                      ? PortTab.BerthOrders
                      : PortTab.PortCalls,
                  )
                }
                icon={
                  tab === PortTab.PortCalls
                    ? CalendarViewWeekIcon
                    : DirectionsBoatIcon
                }
                offset={100}
              />
            </Tooltip>
          </>
        )}
        <Resizable
          enable={{
            bottom: false,
            bottomLeft: false,
            right: true,
            bottomRight: false,
            left: false,
            top: false,
            topLeft: false,
            topRight: false,
          }}
          size={{
            width,
            height,
          }}
          maxWidth={parentWidth - 36}
          minWidth={theme.mapDrawer.width}
          onResizeStop={(_, __, ___, d) => {
            setWidth(width + d.width);
          }}
          onResize={(_, __, ___, delta) => {
            setTempWidth(width + delta.width);
          }}
        >
          <Paper
            className={classNames(classes.drawerPaper, classes.drawerContent)}
            elevation={5}
          >
            <Choose>
              <Choose.When condition={tab === PortTab.PortCalls}>
                <MapPortCallsDrawerContent
                  port={port}
                  map={map}
                  closeDrawer={handleDrawerClose}
                  containerHeight={height}
                />
              </Choose.When>
              <Choose.When condition={tab === PortTab.BerthOrders}>
                <PortBerthOrders portId={port.id} containerHeight={height} />
              </Choose.When>
            </Choose>
          </Paper>
        </Resizable>
      </Drawer>
    </div>
  );
};

export default MapPortCallsDrawer;
